/**
 * Convert selection block conditions to Formly hideExpression
 * Handles: radiobuttons, radiobuttons2, dropdown, dropdown2 blocks
 */

export interface SelectionConditionProps {
	mode?: 'is-matched' | 'is-not-matched' | 'in-array' | 'is-selected';
	match?: string;
	option?: any; // Can be string (option id) or object with id
	button?: any; // Can be string (button id/value) or object with id/value
	isDynamic?: boolean;
}

/**
 * Convert a selection condition (radio button or dropdown) to hideExpression
 * Returns expression that evaluates to TRUE when condition is MET
 * (Remember: hideExpression hides when TRUE, so we'll invert later)
 */
export function convertSelectionCondition(
	fieldKey: string,
	props: SelectionConditionProps,
	conditionType: string
): string | undefined {
	const { mode, match, option, button, isDynamic } = props;

	// Handle radio button conditions (radiobuttons-condition, radiobuttons-condition2)
	if (
		conditionType === 'radiobuttons-condition' ||
		conditionType === 'radiobuttons-condition2' ||
		conditionType === 'radiobuttons-condition3'
	) {
		return convertRadioButtonCondition(fieldKey, props);
	}

	// Handle dropdown conditions (dropdown-condition, dropdown-condition2)
	if (conditionType === 'dropdown-condition' || conditionType === 'dropdown-condition2') {
		return convertDropdownCondition(fieldKey, props, conditionType);
	}

	return undefined;
}

/**
 * Convert radio button condition
 */
function convertRadioButtonCondition(fieldKey: string, props: SelectionConditionProps): string | undefined {
	const { button, isDynamic } = props;

	if (!button) {
		return undefined;
	}

	// Extract button value (can be string or object with id/value/name)
	let buttonValue: string;
	if (typeof button === 'string') {
		buttonValue = button;
	} else if (button.value) {
		buttonValue = button.value;
	} else if (button.name) {
		buttonValue = button.name;
	} else if (button.id) {
		buttonValue = button.id;
	} else {
		return undefined;
	}

	// For dynamic radio buttons, we'd need special handling (typically not common)
	// For now, treat like static
	if (isDynamic) {
		// Dynamic radio buttons store complex objects - might need custom logic
		// For now, fall back to simple comparison
	}

	// Check if the field value equals the button value
	return `model['${fieldKey}'] === ${formatValue(buttonValue)}`;
}

/**
 * Convert dropdown condition
 */
function convertDropdownCondition(
	fieldKey: string,
	props: SelectionConditionProps,
	conditionType: string
): string | undefined {
	const { mode, match, option, isDynamic } = props;

	// dropdown-condition2 uses mode-based logic
	if (conditionType === 'dropdown-condition2') {
		switch (mode) {
			case 'is-matched':
				// Field value matches the specified value
				if (match === undefined || match === null) {
					return undefined;
				}
				return `model['${fieldKey}'] === ${formatValue(match)}`;

			case 'is-not-matched':
				// Field value does NOT match the specified value
				if (match === undefined || match === null) {
					return undefined;
				}
				return `model['${fieldKey}'] !== ${formatValue(match)}`;

			case 'in-array': {
				// Field value is in a pipe-separated list
				if (!match) {
					return undefined;
				}
				// Split by pipe and create OR condition
				const values = match.split('|').map((v) => v.trim());
				const conditions = values.map((v) => `model['${fieldKey}'] === ${formatValue(v)}`);
				return `(${conditions.join(' || ')})`;
			}

			case 'is-selected':
				// For dynamic options - check if option is selected
				// This is complex and may require custom implementation
				// For now, just check if field has any value
				if (isDynamic) {
					return `!!(model['${fieldKey}'])`;
				}
				return undefined;

			default:
				return undefined;
		}
	}

	// dropdown-condition (v1) - simpler logic
	if (conditionType === 'dropdown-condition') {
		if (!mode) {
			// No mode specified - check if specific option is selected
			if (option) {
				let optionValue: string;
				if (typeof option === 'string') {
					optionValue = option;
				} else if (option.value) {
					optionValue = option.value;
				} else if (option.name) {
					optionValue = option.name;
				} else if (option.id) {
					optionValue = option.id;
				} else {
					return undefined;
				}
				return `model['${fieldKey}'] === ${formatValue(optionValue)}`;
			}
			return undefined;
		}

		// Mode-based logic
		switch (mode) {
			case 'is-matched':
				if (match === undefined || match === null) {
					return undefined;
				}
				return `model['${fieldKey}'] === ${formatValue(match)}`;

			case 'is-not-matched':
				if (match === undefined || match === null) {
					return undefined;
				}
				return `model['${fieldKey}'] !== ${formatValue(match)}`;

			default:
				return undefined;
		}
	}

	return undefined;
}

/**
 * Format a value for use in JavaScript expression
 */
function formatValue(value: any): string {
	if (value === null || value === undefined) {
		return 'null';
	}

	if (typeof value === 'string') {
		// Escape single quotes
		const escaped = value.replace(/'/g, "\\'");
		return `'${escaped}'`;
	}

	if (typeof value === 'number') {
		return String(value);
	}

	if (typeof value === 'boolean') {
		return String(value);
	}

	// For objects or other types, convert to JSON string
	return JSON.stringify(value);
}
